import type {
  FcmTokenRegisterRequest,
  GetNotificationSettingsData,
  PushSettingRequest,
  RegisterFcmTokenData,
  UpdatePushNotificationData,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'

import {
  adaptNotificationPage,
  adaptPushNotificationSetting,
  adaptUnreadNotificationCount,
} from './adapters'
import type {
  Notification,
  NotificationCta,
  NotificationPage,
  PushNotificationSetting,
} from './schemas'

// 메시징 서버는 CommonResponse 봉투 없이 본문을 그대로 낸다. 생성 타입이 없어 본문을 unknown 으로 받는다.

export type NotificationListParams = {
  partitionKey: Notification['partitionKey']
  size: number
  /** sortKey 보다 앞(BEFORE)·뒤(AFTER)를 찾는다. sortKey 가 없으면 최신부터 */
  searchType: 'BEFORE' | 'AFTER'
  sortKey?: Notification['sortKey']
}

export const getNotifications = async ({
  partitionKey,
  ...params
}: NotificationListParams): Promise<NotificationPage> => {
  const body = await httpClient('messaging').get<unknown>(`/${partitionKey}`, { params })
  return adaptNotificationPage(body)
}

export const getUnreadNotificationCount = async (
  partitionKey: Notification['partitionKey'],
): Promise<number> => {
  const body = await httpClient('messaging').get<unknown>(`/${partitionKey}/unread-count`)
  return adaptUnreadNotificationCount(body)
}

/** sortKey 를 빼면 전부 읽음으로 바꾼다 */
export const readNotifications = async (
  partitionKey: Notification['partitionKey'],
  sortKey?: Notification['sortKey'],
): Promise<void> => {
  await httpClient('messaging').put<unknown>(`/${partitionKey}/read`, undefined, {
    params: { sortKey },
  })
}

export const deleteNotificationCta = async (
  partitionKey: Notification['partitionKey'],
  sortKey: Notification['sortKey'],
  action: NotificationCta['action'],
): Promise<void> => {
  await httpClient('messaging').delete<unknown>(`/${partitionKey}/${sortKey}/cta/${action}`)
}

/***************** 푸시 알림 (메인 서버) *****************/
// 알림 목록은 메시징 서버지만, 푸시 수신 설정과 기기 토큰은 메인 서버가 관리한다. 설정 조회는 봉투 없이 온다.

export const getPushNotificationSetting = async (
  meta?: RequestMeta,
): Promise<PushNotificationSetting> =>
  adaptPushNotificationSetting(
    await httpClient().get<GetNotificationSettingsData>('/notifications/settings', { meta }),
  )

export const updatePushNotificationSetting = async (
  body: PushSettingRequest,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<UpdatePushNotificationData>('/notifications/settings/push', body, {
    meta,
  })
}

export type FcmTokenRegisterBody = FcmTokenRegisterRequest

/** 이 기기의 FCM 토큰을 등록한다 */
export const registerFcmToken = async (
  body: FcmTokenRegisterBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<RegisterFcmTokenData>('/notifications/fcm-tokens', body, { meta })
}
